import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { FNFResponse } from '../../../../../models/response';
import { IntegrationService } from '../../../../../services/integration.service';

/** Swirepay ids are a fixed prefix plus 32 hex chars. Checking the shape here
 * saves a round trip to Swirepay for an obvious typo. Case-insensitive to match
 * swirepay-service, which accepts upper-case hex too. */
export const SWIREPAY_ACCOUNT_ID_PATTERN = /^account-[0-9a-f]{32}$/i;
export const SWIREPAY_SHOP_GID_PATTERN = /^shop-[0-9a-f]{32}$/i;

/** How often the dialog polls check_status while the menu sync runs. */
const STATUS_POLL_MS = 3000;

type SwirepayStep = 'form' | 'connecting' | 'syncing' | 'success' | 'error';

/**
 * Connect a Swirepay merchant: connect (validate account + shop) -> migrate
 * (queue the menu sync) -> poll check_status until COMPLETED / FAILED. There is
 * no OAuth redirect as with Square / Clover — we call Swirepay as a partner
 * with the merchant's account id, so the owner only types the two ids.
 */
@Component({
  selector: 'app-swirepay-integration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
  ],
  templateUrl: './swirepay-integration.component.html',
  styleUrl: './swirepay-integration.component.css',
})
export class SwirepayIntegrationComponent implements OnInit, OnDestroy {
  integrationService = inject(IntegrationService);
  toastr = inject(ToastrService);
  readonly dialogRef = inject(MatDialogRef<SwirepayIntegrationComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA, { optional: true });

  readonly form = new FormGroup({
    account_id: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(SWIREPAY_ACCOUNT_ID_PATTERN)],
    }),
    shop_gid: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(SWIREPAY_SHOP_GID_PATTERN)],
    }),
  });

  modules: any[] = [
    { name: 'Shop details', iconCheck: false, iconLoading: true, iconDot: false },
    { name: 'Types & Categories', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Customizations', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Menu items', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Finishing setup', iconCheck: false, iconLoading: false, iconDot: true },
  ];

  step: SwirepayStep = 'form';
  shopName = '';
  errorMessage = '';
  /** Shop settings that make Swirepay refuse Fork & Find orders (shop closed
   * every day, pay at store off), from the connect response (FNF-1660). */
  shopWarnings: string[] = [];

  private destroy$ = new Subject<void>();
  private timers: ReturnType<typeof setTimeout>[] = [];

  ngOnInit() {
    // Reconnect pre-fills the ids already on file so the owner only confirms.
    const existing = this.data?.swirepayStatus;
    if (existing?.account_id || existing?.shop_gid) {
      this.form.patchValue({
        account_id: existing.account_id || '',
        shop_gid: existing.shop_gid || '',
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.timers.forEach((t) => clearTimeout(t));
  }

  /** True when the control has been touched and fails `error`. */
  showError(control: 'account_id' | 'shop_gid', error: string): boolean {
    const ctrl = this.form.controls[control];
    return (ctrl.touched || ctrl.dirty) && ctrl.hasError(error);
  }

  connectSwirepay() {
    // Pasted ids often carry stray whitespace; trim before validating.
    this.form.setValue({
      account_id: this.form.controls.account_id.value.trim(),
      shop_gid: this.form.controls.shop_gid.value.trim(),
    });
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.step = 'connecting';
    this.errorMessage = '';
    this.integrationService
      .swirepayConnect(this.form.getRawValue())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response?.data) {
            // Bad ids or a Swirepay rejection: back to the form so the owner
            // can correct them, with the service's reason when it gave one.
            this.step = 'form';
            this.errorMessage = this.errorText(response);
            this.toastr.error(this.errorMessage, 'Error');
            return;
          }
          this.shopName = response.data.shop_name || '';
          this.shopWarnings = (response.data.warnings || []).filter(
            (w: unknown): w is string => typeof w === 'string' && w.trim() !== '',
          );
          this.startMigration();
        },
        error: () => {
          this.step = 'form';
          this.errorMessage = 'Unable to complete your action. Please try again later.';
          this.toastr.error(this.errorMessage, 'Error');
        },
      });
  }

  startMigration() {
    this.step = 'syncing';
    this.animateModules();
    this.integrationService
      .swirepayMigrate()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response?.data) {
            this.displayError(this.errorText(response));
            return;
          }
          this.pollStatus();
        },
        error: () => this.displayError(),
      });
  }

  pollStatus() {
    this.integrationService
      .getSwirepayStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          const status = response?.data?.status;
          if (status === 'COMPLETED') {
            this.modules.forEach((m) => {
              m.iconCheck = true;
              m.iconLoading = false;
              m.iconDot = false;
            });
            this.step = 'success';
          } else if (status === 'FAILED') {
            this.displayError();
          } else {
            // IN_PROGRESS / CONNECTED, or a transient error: keep polling, the
            // sync runs in the background on swirepay-service either way.
            this.timers.push(setTimeout(() => this.pollStatus(), STATUS_POLL_MS));
          }
        },
        error: () => this.timers.push(setTimeout(() => this.pollStatus(), STATUS_POLL_MS)),
      });
  }

  retry() {
    this.step = 'form';
    this.errorMessage = '';
  }

  finishSetup() {
    this.dialogRef.close('finishSetup');
  }

  /** Same cosmetic progress as the Clover / Square dialogs: the service only
   * reports IN_PROGRESS, so the steps advance on a timer until COMPLETED. */
  private animateModules() {
    for (let i = 0; i < this.modules.length - 1; i++) {
      this.timers.push(
        setTimeout(
          () => {
            this.modules[i].iconCheck = true;
            this.modules[i].iconLoading = false;
            this.modules[i + 1].iconLoading = true;
            this.modules[i + 1].iconDot = false;
          },
          (i + 1) * 5000,
        ),
      );
    }
  }

  private displayError(message?: string) {
    this.errorMessage = message || '';
    this.step = 'error';
  }

  private errorText(response: any): string {
    // The service catches HTTP errors and hands back the HttpErrorResponse, so
    // for a 422 / 409 / 502 `response.error` is the Kong envelope body and the
    // Swirepay reason sits in its `error.message` or `error.detail`. A 200 with
    // no data carries the ApiError directly in `response.error`. FastAPI puts a
    // list in `detail` for request validation errors; only text is shown.
    const envelope = response?.error;
    return (
      this.textOf(envelope?.error?.message) ||
      this.textOf(envelope?.error?.detail) ||
      this.textOf(envelope?.detail) ||
      this.textOf(envelope?.message) ||
      'Unable to connect Swirepay. Check the merchant account id and shop id.'
    );
  }

  private textOf(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    const nested = (value as { message?: unknown } | null)?.message;
    return typeof nested === 'string' ? nested.trim() : '';
  }
}
